import { useCallback, useState } from "react";
import { ContractNature, NeedPriority } from "../../autogen/bff-api";
import { useStructuredCompletion } from "../../common/ai/useAiCompletion";

export type NeedTypeSuggestion = {
  procurementType: ContractNature;
  priority: NeedPriority;
  reasoning: string;
};



const SUGGESTION_SCHEMA = JSON.stringify({
  type: "object",
  additionalProperties: false,
  properties: {
    procurementType: {
      type: "string",
      enum: ["SUPPLIES", "SERVICES", "WORKS"],
      description: "SUPPLIES for goods/products, SERVICES for services/advisory, WORKS for building and construction.",
    },
    priority: {
      type: "string",
      enum: ["HIGH", "MEDIUM", "LOW"],
      description: "Estimated urgency of the need.",
    },
    reasoning: { type: "string", description: "A short justification in Norwegian, max ~25 words." },
  },
  required: ["procurementType", "priority", "reasoning"],
});

export const useNeedTypeSuggestion = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const runStructured = useStructuredCompletion({ feature: "need-type-suggestion" });

  const suggest = useCallback(
    async (title: string, description: string): Promise<NeedTypeSuggestion | null> => {
      setIsLoading(true);
      setHasError(false);
      try {
        const json = await runStructured({ jsonSchema: SUGGESTION_SCHEMA, messages: buildMessages(title, description) });
        return parseSuggestion(json);
      } catch {
        setHasError(true);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [runStructured],
  );

  return { suggest, isLoading, hasError };
};

const buildMessages = (title: string, description: string) => [
  {
    role: "SYSTEM" as const,
    content: [
      "You are an assistant for Norwegian public procurement.",
      "Given a procurement need's title and description, classify the contract type and estimate the priority.",
      "Write the reasoning in Norwegian.",
    ].join(" "),
  },
  {
    role: "USER" as const,
    content: [`Title: ${title || "(no title)"}`, `Description:\n${description || "(no description)"}`].join("\n\n"),
  },
];

const TYPES: Record<string, ContractNature> = {
  SUPPLIES: ContractNature.Supplies,
  SERVICES: ContractNature.Services,
  WORKS: ContractNature.Works,
};

const PRIORITIES: Record<string, NeedPriority> = {
  HIGH: NeedPriority.High,
  MEDIUM: NeedPriority.Medium,
  LOW: NeedPriority.Low,
};

const parseSuggestion = (json: string): NeedTypeSuggestion | null => {
  let data: unknown;
  try {
    data = JSON.parse(json);
  } catch {
    return null;
  }
  const obj = data as { procurementType?: unknown; priority?: unknown; reasoning?: unknown };
  const procurementType = TYPES[String(obj.procurementType ?? "")];
  const priority = PRIORITIES[String(obj.priority ?? "")];
  if (!procurementType || !priority) return null;
  return { procurementType, priority, reasoning: String(obj.reasoning ?? "").trim() };
};
